import { Flex, Box, useColorMode, IconButton, Text, Divider, Tooltip } from '@chakra-ui/react';
import {
  SignOut,
  CaretLeft,
  CaretRight,
  ChartLineUp,
  Users,
  Train,
  Buildings,
  Ticket,
  Package,
  ArrowsClockwise,
  ChartBar,
  Broadcast,
  TagChevron,
  HouseSimple,
  Receipt,
  Megaphone,
  BellRinging,
  Sparkle,
  Crosshair,
  Calculator,
  SlidersHorizontal,
} from '@phosphor-icons/react';
//@ts-ignore
import { Link as ReachLink, navigate, useLocation } from '@reach/router';

import { useUser } from '../../contexts/UserContext';
import { useSidebar } from '../../contexts/SidebarContext';
import WideLogo from '../../svgs/WideLogo';
import { isAccessPointMigrationMonitorEnabled } from '../../routes/AccessPoints/Migration/config';

export const SidebarLink = ({ to, children, onMobileClose, isCollapsed = false, label = '' }) => {
  const location = useLocation();
  const { colorMode } = useColorMode();

  const isCurrent = location.pathname.includes(to);

  const navigateTo = (url) => {
    onMobileClose();
    navigate(url);
  };

  const onKeyPress = (event) => {
    if (event.charCode === 13 || event.keyCode === 13) {
      navigateTo(to);
    }
  };

  const content = (
    <Box
      width="100%"
      bg={colorMode === 'light' ? (isCurrent ? 'gray.100' : 'transparent') : isCurrent ? 'gray.700' : 'transparent'}
      transition="all 0.3s ease"
      onClick={() => navigateTo(to)}
      cursor="pointer"
      onKeyPress={onKeyPress}
      tabIndex={0}
      _hover={{
        backgroundColor: colorMode === 'light' ? 'gray.200' : 'gray.700',
      }}
      _focusWithin={{
        outline: 'none',
        backgroundColor: colorMode === 'light' ? 'gray.200' : 'gray.700',
      }}
    >
      <Box role="button" fontWeight={isCurrent ? 'semibold' : 'normal'}>
        <Box width="100%" display="flex" alignItems="center" justifyContent={isCollapsed ? 'center' : 'flex-start'} py="3" px="4">
          {children}
        </Box>
      </Box>
    </Box>
  );

  if (isCollapsed && label) {
    return (
      <Tooltip label={label} placement="right" hasArrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

const SectionHeader = ({ icon, label, isActive, colorMode, isCollapsed }) => {
  const content = (
    <Box
      width="100%"
      bg={colorMode === 'light' ? (isActive ? 'gray.50' : 'transparent') : isActive ? 'gray.800' : 'transparent'}
      tabIndex={-1}
    >
      <Box fontWeight={isActive ? 'semibold' : 'normal'}>
        <Box width="100%" display="flex" alignItems="center" justifyContent={isCollapsed ? 'center' : 'flex-start'} py="3" px="4">
          {icon}
          {!isCollapsed && (
            <Box as="span" ml="2">
              {label}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  if (isCollapsed) {
    return (
      <Tooltip label={label} placement="right" hasArrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

const Sidebar = ({ onMobileClose }) => {
  const location = useLocation();
  const isCurrent = (to) => location.pathname.includes(to);
  const { checkAccess, logOut } = useUser();
  const { colorMode } = useColorMode();
  const { isCollapsed, toggleSidebar } = useSidebar();

  return (
    <Flex flexDir="column" borderRightWidth="1px" zIndex={3} bg={colorMode === 'light' ? 'white' : 'gray.900'} maxHeight="100vh">
      <Flex
        px={isCollapsed ? '2' : '4'}
        height="64px"
        alignItems="center"
        justifyContent={isCollapsed ? 'center' : 'space-between'}
        display={['none', 'none', 'flex']}
      >
        {!isCollapsed && (
          <ReachLink to="/">
            <WideLogo />
          </ReachLink>
        )}
        <IconButton
          size="sm"
          fontSize="lg"
          variant="ghost"
          icon={isCollapsed ? <CaretRight /> : <CaretLeft />}
          aria-label={isCollapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
          onClick={toggleSidebar}
        />
      </Flex>
      <Box flex="1" overflowY="auto">
        {checkAccess('EMPEROR') ? (
          <>
            <SidebarLink to="/order66" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Statistiken">
              <ChartLineUp size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  Statistiken
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}
        {checkAccess('USERS_VIEW') ? (
          <>
            <Box>
              <SidebarLink to="/users" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Benutzer">
                <Users size={20} />
                {!isCollapsed && (
                  <Box as="span" ml="2">
                    Benutzer
                  </Box>
                )}
              </SidebarLink>
              <Box>
                <SidebarLink to="/active-droppoints" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Active DropPoints">
                  <Train size={16} />
                  {!isCollapsed && (
                    <Box as="span" ml="2" fontSize="sm">
                      Active DropPoints
                    </Box>
                  )}
                </SidebarLink>
              </Box>
            </Box>
            <Divider />
          </>
        ) : null}
        {checkAccess('BUSINESS_VIEW') || checkAccess('SPRINT_VIEW') || checkAccess('COMPANY_COUPONS_VIEW') ? (
          <SectionHeader
            icon={<Buildings size={20} />}
            label="Unternehmen"
            isActive={isCurrent('companies')}
            colorMode={colorMode}
            isCollapsed={isCollapsed}
          />
        ) : null}
        {checkAccess('BUSINESS_VIEW') ? (
          <SidebarLink to="/companies/business" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="BUSINESS">
            <Buildings size={16} />
            {!isCollapsed && (
              <Box as="span" ml="2" fontSize="sm">
                BUSINESS
              </Box>
            )}
          </SidebarLink>
        ) : null}
        {checkAccess('SPRINT_VIEW') ? (
          <SidebarLink to="/companies/sprint" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="SPRINT">
            <Buildings size={16} />
            {!isCollapsed && (
              <Box as="span" ml="2" fontSize="sm">
                SPRINT
              </Box>
            )}
          </SidebarLink>
        ) : null}
        {checkAccess('COMPANY_COUPONS_VIEW') ? (
          <SidebarLink to="/company-coupons" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Unternehmen Coupons">
            <Ticket size={16} />
            {!isCollapsed && (
              <Box as="span" ml="2" fontSize="sm">
                Unternehmen Coupons
              </Box>
            )}
          </SidebarLink>
        ) : null}
        {checkAccess('BUSINESS_VIEW') || checkAccess('SPRINT_VIEW') ? <Divider /> : null}

        {checkAccess('UPS') ? (
          <>
            <SectionHeader
              icon={<Package size={20} />}
              label="Access Points"
              isActive={isCurrent('ups')}
              colorMode={colorMode}
              isCollapsed={isCollapsed}
            />
            <SidebarLink to="/ups/access-points/list" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Access Point Liste">
              <Train size={16} />
              {!isCollapsed && (
                <Box as="span" ml="2" fontSize="sm">
                  Access Point Liste
                </Box>
              )}
            </SidebarLink>
            <SidebarLink
              to="/ups/access-points/parcels"
              onMobileClose={onMobileClose}
              isCollapsed={isCollapsed}
              label="Paket Einzelnachweis"
            >
              <Package size={16} />
              {!isCollapsed && (
                <Box as="span" ml="2" fontSize="sm">
                  Paket Einzelnachweis
                </Box>
              )}
            </SidebarLink>
            <SidebarLink
              to="/ups/access-points/activity-pool"
              onMobileClose={onMobileClose}
              isCollapsed={isCollapsed}
              label="Activity Pool"
            >
              <ArrowsClockwise size={16} />
              {!isCollapsed && (
                <Box as="span" ml="2" fontSize="sm">
                  Activity Pool
                </Box>
              )}
            </SidebarLink>
            {isAccessPointMigrationMonitorEnabled() ? (
              <SidebarLink to="/ups/access-points/migration" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Migration">
                <ArrowsClockwise size={16} />
                {!isCollapsed && (
                  <Box as="span" ml="2" fontSize="sm">
                    Migration
                  </Box>
                )}
              </SidebarLink>
            ) : null}
            <SidebarLink to="/reconciliation" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Gutschrift Abgleich">
              <ChartBar size={16} />
              {!isCollapsed && (
                <Box as="span" ml="2" fontSize="sm">
                  Gutschrift Abgleich
                </Box>
              )}
            </SidebarLink>
            <SidebarLink to="/ups/api-monitor" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="API Monitor">
              <Broadcast size={16} />
              {!isCollapsed && (
                <Box as="span" ml="2" fontSize="sm">
                  API Monitor
                </Box>
              )}
            </SidebarLink>
            <Divider />
            <SectionHeader
              icon={<HouseSimple size={20} />}
              label="Hub Counters"
              isActive={isCurrent('amazon')}
              colorMode={colorMode}
              isCollapsed={isCollapsed}
            />
            <SidebarLink to="/amazon/hub-counters/list" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Hub Counter Liste">
              <Train size={16} />
              {!isCollapsed && (
                <Box as="span" ml="2" fontSize="sm">
                  Hub Counter Liste
                </Box>
              )}
            </SidebarLink>
            <SidebarLink
              to="/amazon/hub-counters/parcels"
              onMobileClose={onMobileClose}
              isCollapsed={isCollapsed}
              label="Amazon Paket Einzelnachweis"
            >
              <Package size={16} />
              {!isCollapsed && (
                <Box as="span" ml="2" fontSize="sm">
                  Amazon Paket Einzelnachweis
                </Box>
              )}
            </SidebarLink>
            <SidebarLink to="/amazon/api-monitor" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="API Monitor">
              <Broadcast size={16} />
              {!isCollapsed && (
                <Box as="span" ml="2" fontSize="sm">
                  API Monitor
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}

        {checkAccess('EMPEROR') ? (
          <>
            <SidebarLink to="/app-features" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="App Features">
              <SlidersHorizontal size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  App Features
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}
        {checkAccess('EMPEROR') ? (
          <>
            <SidebarLink to="/orders" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Bestellungen">
              <Package size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  Bestellungen
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}
        {checkAccess('USER_COUPONS_VIEW') || checkAccess('DROPPOINT_COUPONS_VIEW') ? (
          <SectionHeader
            icon={<Ticket size={20} />}
            label="Coupons"
            isActive={isCurrent('coupons')}
            colorMode={colorMode}
            isCollapsed={isCollapsed}
          />
        ) : null}
        {checkAccess('USER_COUPONS_VIEW') ? (
          <SidebarLink to="/coupons" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Besteller Coupons">
            <TagChevron size={16} />
            {!isCollapsed && (
              <Box as="span" ml="2" fontSize="sm">
                Besteller Coupons
              </Box>
            )}
          </SidebarLink>
        ) : null}
        {checkAccess('DROPPOINT_COUPONS_VIEW') ? (
          <SidebarLink to="/station-coupons" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="DropPoint Coupons">
            <HouseSimple size={16} />
            {!isCollapsed && (
              <Box as="span" ml="2" fontSize="sm">
                DropPoint Coupons
              </Box>
            )}
          </SidebarLink>
        ) : null}
        {checkAccess('USER_COUPONS_VIEW') || checkAccess('DROPPOINT_COUPONS_VIEW') ? <Divider /> : null}
        {checkAccess('INVOICES_VIEW') ? (
          <>
            <SidebarLink to="/invoices" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Rechnungen">
              <Receipt size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  Rechnungen
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}
        {checkAccess('ADBERTISEMENT_VIEW') ? (
          <>
            <SidebarLink to="/advertisements" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Werbeverwaltung">
              <Megaphone size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  Werbeverwaltung
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}
        {checkAccess('PUSH_VIEW') ? (
          <>
            <SidebarLink to="/push-notifications" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Push Notifications">
              <BellRinging size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  Push Notifications
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}

        {checkAccess('ALDERAAN_VIEW') ? (
          <>
            <SectionHeader
              icon={<Sparkle size={20} />}
              label="Sales"
              isActive={isCurrent('sales')}
              colorMode={colorMode}
              isCollapsed={isCollapsed}
            />
            <SidebarLink to="/alderaan" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Alderaan">
              <Crosshair size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  Alderaan
                </Box>
              )}
            </SidebarLink>
            <SidebarLink to="/potential-calculator" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="Potenzialrechner">
              <Calculator size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  Potenzialrechner
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}

        {checkAccess('DHL') ? (
          <>
            <SidebarLink to="/dhl" onMobileClose={onMobileClose} isCollapsed={isCollapsed} label="DHL Zonen">
              <HouseSimple size={20} />
              {!isCollapsed && (
                <Box as="span" ml="2">
                  DHL Zonen
                </Box>
              )}
            </SidebarLink>
            <Divider />
          </>
        ) : null}
      </Box>

      <Tooltip label="Logout" placement="right" hasArrow isDisabled={!isCollapsed}>
        <Flex
          alignItems="center"
          justifyContent={isCollapsed ? 'center' : 'flex-start'}
          py="3"
          px={isCollapsed ? '2' : '4'}
          cursor="pointer"
          onClick={logOut}
        >
          <IconButton
            p="1"
            cursor="pointer"
            onClick={logOut}
            display={['none', 'none', 'block']}
            size="sm"
            fontSize="xl"
            variant="ghost"
            icon={<SignOut />}
            aria-label="Ausloggen"
          />
          {!isCollapsed && 'Logout'}
        </Flex>
      </Tooltip>
    </Flex>
  );
};

export default Sidebar;
